import { Stack, Box } from '@mui/material';
import { forwardRef, useCallback, MutableRefObject } from 'react';

import { LoyaltyCurrencyChange } from '@ocx/feat-loyalty-currency';
import {
  Typography,
  SizingMultiplier,
  DynamicCard,
  DynamicCardAccessory,
  DynamicCardContent,
  DynamicCardMedia,
  DynamicCardSlot,
} from '@ocx/ui';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useImpressionTracking } from '@ocx-app/modules/offers/use-impression-tracking.hook';

import { OfferExpirationChip, OfferLabel, AvailableAtLocations } from './components';
import { SurgeonWarning } from '../surgeon-warning/surgeon-warning.component';
import { OfferTitle } from './components/offer-title.component';
import { OfferCardProps } from './offer-card.types';
import { OfferProgressOverTime } from '../offer-progress-over-time/offer-progress-over-time.component';

export const OfferCard = forwardRef<HTMLDivElement, OfferCardProps>((props: OfferCardProps, ref) => {
  const {
    variant = 'carousel',
    loading = false,
    impressionTrackingCtx,
    expiresIn,
    rewardExpiresIn,
    marketingContent: { title, shortDescription, imageSrc, surgeonGeneralsWarning },
    label,
    pointsDiff,
    progress,
    onClick,
    accessoryType = 'none',
    onAccessoryButtonClick,
    accessoryButtonDisabled,
    pointsStrategy,
    availableAtLocations,
  } = props;

  const { ref: trackingRef } = useImpressionTracking(impressionTrackingCtx);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      trackingRef(node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        const mutableRef = ref as MutableRefObject<HTMLDivElement | null>;
        mutableRef.current = node;
      }
    },
    [trackingRef, ref],
  );

  return (
    <DynamicCard
      ref={setRefs}
      loading={loading}
      onClick={onClick}
      sx={{
        border: variant === 'sheet' ? (theme) => `1px ${theme.palette.divider} solid` : undefined,
      }}>
      {surgeonGeneralsWarning && (
        <Box
          sx={(theme) => ({
            m:
              variant !== 'banner'
                ? theme.spacing(SizingMultiplier.md, SizingMultiplier.md, -SizingMultiplier.sm)
                : SizingMultiplier.sm,
          })}>
          <SurgeonWarning message={surgeonGeneralsWarning} square={variant === 'banner'} />
        </Box>
      )}
      <Box position="relative">
        {variant === 'banner' && imageSrc && <DynamicCardMedia src={imageSrc} alt={title} variant="banner" />}
        {variant === 'banner' && expiresIn && (
          <DynamicCardSlot positionHorizontal="right">
            <OfferExpirationChip time={expiresIn} />
          </DynamicCardSlot>
        )}
        <DynamicCardContent>
          {variant !== 'banner' && imageSrc && <DynamicCardMedia src={imageSrc} alt={title} />}
          <Stack flex={1} alignItems="flex-start" justifyContent="center" spacing={SizingMultiplier.xs}>
            {variant !== 'banner' && label && <OfferLabel label={label} pointsStrategy={pointsStrategy} />}
            <OfferTitle variant={variant}>{title}</OfferTitle>
            {shortDescription && variant !== 'carousel' && (
              <Typography variant="caption" component="p" color="textSecondary">
                {shortDescription}
              </Typography>
            )}
            {progress?.canShowMemberProgress && (
              <OfferProgressOverTime
                progress={progress.memberProgress}
                variant={progress.progressVariant}
                formatVariant={progress.formatVariant}
              />
            )}
            {(pointsDiff || availableAtLocations) && (
              <Stack direction="row" spacing={SizingMultiplier.sm} alignItems="center">
                {pointsDiff && <LoyaltyCurrencyChange points={pointsDiff.diff} />}
                {availableAtLocations && <AvailableAtLocations count={availableAtLocations.length} />}
              </Stack>
            )}
            {variant === 'banner' && label && <OfferLabel label={label} pointsStrategy={pointsStrategy} />}
            {variant === 'carousel' && rewardExpiresIn && (
              <Typography variant="body2" component="span" color="text.secondary">
                {rewardExpiresIn}
              </Typography>
            )}
          </Stack>
          <DynamicCardAccessory
            type={accessoryType}
            onClick={onAccessoryButtonClick}
            disabled={accessoryButtonDisabled}
          />
        </DynamicCardContent>
      </Box>
    </DynamicCard>
  );
});
